import { Component } from '@theme/component';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  get productPageUrl() {
    return /** @type {HTMLAnchorElement} */ (this.closest('product-card')?.querySelector('a[ref="productCardLink"]'))
      ?.href;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (!this.#cachedProductHtml) {
      await this.fetchProductPage(this.productPageUrl);
    }

    if (this.#cachedProductHtml) {
      // Create a fresh copy of the cached HTML to avoid modifying the original
      const freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        'text/html'
      );

      await this.updateQuickAddModal(freshHtmlCopy);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      // Store the HTML for later use
      this.#cachedProductHtml = html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   */
  async updateQuickAddModal(newHtml) {
    const productGrid = newHtml.querySelector('[data-product-grid-content]');
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    morph(modalContent, productGrid);
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  #isVariantLoading = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Override showDialog to setup request availability button when modal opens
   */
  showDialog() {
    super.showDialog();
    // Setup request availability button after modal is shown
    setTimeout(() => {
      this.#setupRequestAvailabilityButton();
      this.#setupOOSNotificationCloseHandlers();
    }, 100);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  /**
   * Handles variant selected event to track loading state
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    this.#isVariantLoading = true;
  };

  /**
   * Handles variant update event to track loading completion and setup request availability button
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    this.#isVariantLoading = false;
    this.#setupRequestAvailabilityButton();
  };

  /**
   * Sets up the request availability button click handler
   */
  #setupRequestAvailabilityButton = () => {
    const requestAvailabilityButton = this.querySelector('.request-availability__button');
    if (requestAvailabilityButton) {
      // Remove existing event listener to avoid duplicates
      requestAvailabilityButton.removeEventListener('click', this.#handleRequestAvailabilityClick);
      requestAvailabilityButton.addEventListener('click', this.#handleRequestAvailabilityClick);
    }
  };

  /**
   * Handles request availability button click
   * @param {Event} event - The click event
   */
  #handleRequestAvailabilityClick = (event) => {
    event.preventDefault();
    
    // Wait for variant loading to complete before proceeding
    if (this.#isVariantLoading) {
      setTimeout(() => this.#handleRequestAvailabilityClick(event), 200);
      return;
    }

    // Extract current variant data and prefill OOS form
    this.#prefillOOSForm();
    
    // Close the quick-add dialog
    this.closeDialog();
    
    // Show OOS notification
    this.#showOOSNotification();
  };

  /**
   * Extracts current variant data and prefills the OOS form
   */
  #prefillOOSForm = () => {
    const oosNotification = this.refs.oosNotification || document.getElementById('oosNotification');
    if (!oosNotification) return;

    // Get product name from the quick-add modal
    const productTitle = this.querySelector('.view-product-title a')?.textContent?.trim() || 
                        this.querySelector('.product-header a')?.textContent?.trim() || '';
    
    // Get product code from the modal content
    let productCode = '';
    
    // Try to get product code from data attribute
    const productCodeElement = /** @type {HTMLElement} */ (this.querySelector('[data-product-code]'));
    if (productCodeElement?.dataset.productCode) {
      productCode = productCodeElement.dataset.productCode;
    }

    // Get selected variant options
    const variantPicker = this.querySelector('variant-picker');
    if (!variantPicker) return;

    let selectedColor = '';
    let selectedSize = '';

    // Extract selected color and size from variant picker
    const selectedOptions = variantPicker.querySelectorAll('input:checked, select option[selected]');
    selectedOptions.forEach(option => {
      if (!(option instanceof HTMLInputElement || option instanceof HTMLOptionElement)) return;
      
      let optionName = '';
      let optionValue = '';
      
      if (option instanceof HTMLInputElement) {
        optionName = option.name.toLowerCase();
        optionValue = option.value;
        
        // Also check the fieldset legend for better option identification
        const fieldset = option.closest('fieldset');
        if (fieldset) {
          const legend = fieldset.querySelector('legend');
          if (legend) {
            const legendText = legend.textContent?.toLowerCase().replace(':', '').trim() || '';
            if (legendText) optionName = legendText;
          }
        }
      } else if (option instanceof HTMLOptionElement) {
        const select = option.closest('select');
        if (select) {
          optionName = select.name?.toLowerCase() || '';
          // Look for label associated with select
          const label = select.closest('.variant-option')?.querySelector('label');
          if (label) {
            optionName = label.textContent?.toLowerCase().replace(':', '').trim() || optionName;
          }
        }
        optionValue = option.value || option.textContent?.trim() || '';
      }
      
      // Check for color/colour variants
      if (optionName.includes('color') || optionName.includes('colour')) {
        selectedColor = optionValue;
      } 
      // Check for size variants
      else if (optionName.includes('size')) {
        selectedSize = optionValue;
      }
    });

    // Prefill the OOS form fields
    if (!(oosNotification instanceof HTMLElement)) return;
    
    const productNameField = /** @type {HTMLInputElement} */ (oosNotification.querySelector('#placeorder-product-name'));
    const productCodeField = /** @type {HTMLInputElement} */ (oosNotification.querySelector('#placeorder-code'));
    const colorField = /** @type {HTMLInputElement} */ (oosNotification.querySelector('#placeorder-color'));
    const sizeField = /** @type {HTMLInputElement} */ (oosNotification.querySelector('#placeorder-size'));

    if (productNameField) productNameField.value = productTitle;
    if (productCodeField) productCodeField.value = productCode;
    if (colorField) colorField.value = selectedColor;
    if (sizeField) sizeField.value = selectedSize;
  };

  /**
   * Shows the OOS notification
   */
  #showOOSNotification = () => {
    const oosNotification = this.refs.oosNotification || document.getElementById('oosNotification');
    if (oosNotification && oosNotification instanceof HTMLElement) {
      oosNotification.classList.add('active');
      document.body.classList.add('overflow-hidden');
    }
  };

  /**
   * Sets up OOS notification close handlers
   */
  #setupOOSNotificationCloseHandlers = () => {
    const oosNotification = this.refs.oosNotification || document.getElementById('oosNotification');
    if (!(oosNotification instanceof HTMLElement)) return;

    const closeButton = oosNotification.querySelector('.oos-notification__close');
    const overlay = oosNotification.querySelector('.oos-notification__overlay');

    if (closeButton) {
      closeButton.removeEventListener('click', this.#closeOOSNotification);
      closeButton.addEventListener('click', this.#closeOOSNotification);
    }

    if (overlay) {
      overlay.removeEventListener('click', this.#closeOOSNotification);
      overlay.addEventListener('click', this.#closeOOSNotification);
    }
  };

  /**
   * Closes the OOS notification
   */
  #closeOOSNotification = () => {
    const oosNotification = this.refs.oosNotification || document.getElementById('oosNotification');
    if (oosNotification && oosNotification instanceof HTMLElement) {
      oosNotification.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
    }
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
